import {
	Button,
	Grid,
	Typography,
	Pagination as PaginationBar,
	Spin,
} from 'antd';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { ApplicationSummary } from '../../../../shared/VolunteerApplicationServiceTypes';
import LeftArrowWhite from '../../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../../assets/images/filter-icon.svg';
import Icon from '../../../../components/ui-components/Icon';
import { useCallback, useState } from 'react';
import { SCREENING_DASHBOARD } from '../../../../shared/Routes';
import { useBatchId } from '../../../../hooks/useBatchId';
import { FilterProposerReassign } from './FilterProposerReassign';
import { ProposerReassignTable } from './ProposerReassignTable';
import { ProposerReassignCard } from './ProposerReassignCard';
import { useProposerReassignPage } from './useProposerReassign';
import { DEFAULT_PAGE_SIZE } from '../../../../shared/Constants';
import { UndoOutlined } from '@ant-design/icons';
import EmptyList from '../../../../components/EmptyList';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ProposerReassign = () => {
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();
	const {
		onSubmitClick,
		functionaryList: { functionary, pagination },
		paging,
		onPageChange,
		onFilterChange,
		filter,
		isFilterTextApplied,
		setIsFilterTextApplied,
		loading,
	} = useProposerReassignPage();

	const [resetFilterValues, setResetFilterValues] = useState<boolean>(false);
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);

	const applicationRecord: ApplicationSummary | null = JSON.parse(
		sessionStorage.getItem('SELECTED_APPLICATION') ?? 'null'
	) as ApplicationSummary | null;

	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	const handleBackClick = useCallback(() => {
		navigateToRoute(SCREENING_DASHBOARD);
	}, [navigateToRoute]);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	const resetFunctionaryFilters = useCallback(() => {
		onFilterChange({});
		setResetFilterValues(true);
		setIsFilterTextApplied(false);
	}, [onFilterChange, setIsFilterTextApplied]);

	const proposerReassignCards = functionary?.map((proposer) => (
		<div className="mb-[16px]" key={proposer.srcmId}>
			<ProposerReassignCard
				{...proposer}
				onSubmitClick={() => onSubmitClick(proposer.id as number)}
			/>
		</div>
	));

	return (
		<div className="flex flex-col p-[16px]">
			<div
				className={`flex gap-8 ${
					isMobile ? 'justify-center align-center' : ''
				}`}
			>
				<Text className="font-sans font-bold text-xl text-secondary-black mb-4">
					{t('listOfProposerReassign')}
				</Text>
			</div>
			<div className="flex justify-between">
				<div className="sm:mt-0 md:mt-[16px] flex gap-4">
					{/* Back Button - Web */}
					<Button
						type="primary"
						onClick={handleBackClick}
						className="hidden md:block font-bold text-sm"
					>
						<span className="flex gap-2">
							<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
							{t('back')}
						</span>
					</Button>
					<Text className="hidden md:block font-sans font-bold text-xl text-secondary-black mb-4">
						{applicationRecord?.center?.zone}
					</Text>

					{/* Back Button - Mobile */}
					<div className="block sm:hidden">
						<a
							onClick={handleBackClick}
							className="relative top-[8px] md:top-[3px] mr-2 font-bold text-sm"
						>
							<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
						</a>
					</div>
				</div>
				<div className="sm:mt-0 md:mt-[16px] flex sm:justify-start md:justify-end">
					{/* Filter Button - mobile */}
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					{/* Add Filter */}
					{/* Reset Filter Button - Web */}
					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={resetFunctionaryFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid border-primary-blue`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>
			</div>
			<div className="mt-3 p-3 bg-white rounded-lg flex items-center border border-solid border-gray-200 w-full">
				<div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 w-full">
					<div className="flex flex-col gap-2">
						<Text className="text-gray-500 text-sm">{t('fullName')}</Text>
						<Text className="font-semibold text-gray-800">{`${
							applicationRecord?.applicant?.firstName ?? ''
						} ${applicationRecord?.applicant?.middleName ?? ''} ${
							applicationRecord?.applicant?.lastName ?? ''
						}`}</Text>
					</div>
					<div className="flex flex-col gap-2">
						<Text className="text-gray-500 text-sm">{t('SRCMID')}</Text>
						<Text className="font-semibold text-gray-800">
							{applicationRecord?.applicant?.srcmid}
						</Text>
					</div>
					<div className="flex flex-col gap-2">
						<Text className="text-gray-500 text-sm">{t('gender')}</Text>
						<Text className="font-semibold text-gray-800">
							{applicationRecord?.applicant?.gender}
						</Text>
					</div>
				</div>
			</div>
			<div className="block sm:hidden mt-4">
				<Text className="font-sans font-bold text-[16px] text-secondary-black">
					{`List of ${applicationRecord?.center?.zone ?? ''}`}
				</Text>
			</div>
			<div className="mb-[16px]">
				<FilterProposerReassign
					open={isFilterPopup}
					value={filter}
					onClose={onClose}
					onFilterChange={onFilterChange}
					resetFilterValues={resetFilterValues}
					setResetFilterValues={setResetFilterValues}
				/>
			</div>
			{isMobile ? (
				<>
					{isEmpty(pagination.totalNoOfRecords) ||
					pagination.totalNoOfRecords === 0 ? (
						<EmptyList message={t('noProposerCurrentlyAvailable')} />
					) : (
						<>
							{proposerReassignCards}
							<div className="flex justify-center w-full">
								<PaginationBar
									current={paging.pageNo ?? 1}
									total={pagination.totalNoOfRecords}
									showTotal={(total, range) =>
										`${range[0]}-${range[1]} of ${total} items`
									}
									defaultPageSize={paging.pageSize ?? DEFAULT_PAGE_SIZE}
									onChange={onPageChange}
								/>
							</div>
						</>
					)}
				</>
			) : (
				<>
					{loading ? (
						<Spin />
					) : isEmpty(pagination.totalNoOfRecords) ||
					  pagination.totalNoOfRecords === 0 ? (
						<EmptyList message={t('noProposerCurrentlyAvailable')} />
					) : (
						<ProposerReassignTable
							proposers={functionary}
							paging={paging}
							onSubmitClick={onSubmitClick}
							pagination={pagination}
							onPageChange={onPageChange}
						/>
					)}
				</>
			)}
		</div>
	);
};
